import { useTailwindBreakpoints } from "components/hooks/use-tailwind-breakpoints"

import { Box } from "components/shared/box"
import { ClippedBox } from "components/shared/clipped-box/clipped-box"
import { EnergyIcon } from "components/shared/icons/energy"
import { Lock } from "components/shared/icons/lock"

import { getAbilityDescription, getAbilityType } from "utils/abilities/get-ability-description"
import abilityImages from "utils/abilities/images.json"
import shipAbilityImages from "utils/abilities/ships-images.json"
import clippedCorners from "utils/clipped-corners"

import type { EntityAbility } from "types/entity"
import { EntityAbilitySlot } from "types/entity"

import { AbilityAvatar } from "./ability-avatar"
import { AbilityInfo } from "./ability-info"

// https://botsbits.atlassian.net/wiki/spaces/IT/pages/17825797/RPG+stats+and+progression+for+bots#%D0%92%D0%BB%D0%B8%D1%8F%D0%BD%D0%B8%D0%B5-%D0%BF%D0%B0%D1%83%D0%B5%D1%80%D0%B0
const getPowerForNextLevel = (slot: EntityAbilitySlot, power: number) => {
  const powerForLevels = {
    [EntityAbilitySlot.BASIC]: [1, 3, 30],
    [EntityAbilitySlot.SPECIAL]: [1, 20, 50],
    [EntityAbilitySlot.ULTIMATE]: [10, 40, 70],
    [EntityAbilitySlot.ASSIST_AND_COUNTER]: [60, 80, 100],
  }
  if (!powerForLevels[slot] || powerForLevels[slot][powerForLevels[slot].length - 1] <= power || power >= 100) {
    return 0
  }

  for (const powerForLevel of powerForLevels[slot]) {
    if (powerForLevel > power) {
      return Math.ceil(powerForLevel - power)
    }
  }

  return 0
}

type AbilitiesItemProps = EntityAbility & {
  className: string
  power: number
  damage: number
  energy?: number
  isShip?: boolean
}

export function AbilitiesItem({
  ability,
  level = 1,
  unlocked,
  className = "",
  slot,
  power,
  damage,
  energy,
  isShip = false,
}: AbilitiesItemProps) {
  const { isMobile } = useTailwindBreakpoints()
  const powerForNextLevel = getPowerForNextLevel(slot, power)
  const title = getAbilityType(ability)
  const showEnergy = !!energy
  const description = getAbilityDescription({ level, abilityName: ability, abilityDamage: damage, isShip })
  const imageUrl = isShip
    ? shipAbilityImages[ability as keyof typeof shipAbilityImages]
    : abilityImages[ability as keyof typeof abilityImages]

  return (
    <Box
      row
      className={`gap-2 p-1 pr-3 rounded-2xl relative bg-[#4F4ACC4D] overflow-hidden lg:gap-6 lg:p-4 ${className} ${
        unlocked ? "" : "opacity-50"
      }`}
    >
      {showEnergy && (
        <ClippedBox
          className="absolute bottom-0 pr-6 pl-2 py-1 lg:pr-10 lg:pl-6 bg-[#4f4acc] text-tiny lg:text-table z-1 left-0 rounded-bl-md font-semibold gap-1"
          corners={clippedCorners.rt}
          clipK={isMobile ? "1.8rem" : "2.5rem"}
          row
          center
        >
          <EnergyIcon className="h-4" />
          {energy}
        </ClippedBox>
      )}
      {!unlocked && (
        <Box className="absolute w-[40px] h-[40px] -top-[20px] -right-[20px] p-1 justify-center bg-[#5A54DF] -rotate-[45deg] lg:w-[108px] lg:h-[108px] lg:-top-[54px] lg:-right-[54px] lg:p-2 ">
          <Lock className="rotate-[45deg]" w={isMobile ? 8 : 23} h={isMobile ? 10 : 28} />
        </Box>
      )}
      <AbilityAvatar
        level={level}
        count={level}
        showEnergy={showEnergy}
        // @todo add normal default image
        imageUrl={imageUrl || "/images/pages/bot/main-section/ability.png"}
      />
      <AbilityInfo
        title={title}
        description={Array.isArray(description) ? description : [description]}
        powerForNextLevel={powerForNextLevel}
        level={level}
        unlocked={unlocked}
        isShip={isShip}
      />
    </Box>
  )
}
